import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const UpdateStatusModal = ({ isOpen, onClose, interview, onStatusUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statusOptions = [
    {
      id: 'interviewing',
      label: 'Interviewing',
      description: 'Still in the interview process',
      color: 'bg-blue-500',
      icon: '💼'
    },
    {
      id: 'rejected-first',
      label: 'Rejected at interview (first stage)',
      description: 'Did not progress past initial round',
      color: 'bg-red-500',
      icon: '❌'
    },
    {
      id: 'rejected-final',
      label: 'Rejected at interview (second/final stage)',
      description: 'Did not receive offer after final round',
      color: 'bg-red-500',
      icon: '❌'
    },
    {
      id: 'offer-pending',
      label: 'Received an offer (pending decision)',
      description: 'Considering the offer',
      color: 'bg-yellow-500',
      icon: '📄'
    },
    {
      id: 'offer-accepted',
      label: 'Received an offer (pending decision)',
      description: 'Congratulations! You got the job',
      color: 'bg-green-500',
      icon: '🎉'
    },
    {
      id: 'other',
      label: 'Other',
      description: 'Custom status',
      color: 'bg-gray-500',
      icon: '📝'
    }
  ];

  const handleSubmit = async () => {
    if (!selectedStatus) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const selectedOption = statusOptions.find(option => option.id === selectedStatus);
      
      const updateData = {
        fields: {
          'Candidate Interview Stage': selectedStatus === 'other' ? 'Other' : selectedOption.label,
        },
        interviewId: interview.id
      };

      // If "Other" is selected and there's custom text, update the other field too
      if (selectedStatus === 'other' && otherText.trim()) {
        updateData.fields['Candidate Interview Stage (other)'] = otherText.trim();
      }

      await axios.patch(`/api/interviews/updateInterview`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Call the callback to refresh data
      if (onStatusUpdate) {
        onStatusUpdate();
      }

      onClose();
    } catch (error) {
      console.error('Error updating interview status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus('');
    setOtherText('');
    onClose();
  };

  if (!isOpen || !interview) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl relative max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Update {interview['Name (from Startup)']} Application
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {interview['Role Title']}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {statusOptions.map((option) => (
              <div key={option.id}>
                <label
                  className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all hover:bg-gray-50 ${
                    selectedStatus === option.id
                      ? 'border-[#02B491] bg-[#02B491]/5'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center h-6">
                    <input
                      type="radio"
                      name="status"
                      value={option.id}
                      checked={selectedStatus === option.id}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-4 h-4 text-[#02B491] border-gray-300 focus:ring-[#02B491]"
                    />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{option.icon}</span>
                      <div className={`w-2 h-2 rounded-full ${option.color}`}></div>
                      <span className="font-medium text-gray-900">{option.label}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  </div>
                </label>

                {/* Other text input */}
                {selectedStatus === 'other' && option.id === 'other' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 ml-7"
                  >
                    <textarea
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      placeholder="Please specify the status..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#02B491] focus:border-transparent resize-none"
                      rows={3}
                    />
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedStatus || isSubmitting}
            className="flex-1 px-4 py-3 bg-[#02B491] text-white rounded-lg font-medium hover:bg-[#029078] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              'Update Status'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default UpdateStatusModal; 